import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';

/**
 * Creators Products - Manage creator's products
 */
export const Route = createFileRoute('/_authenticated/creators/products')({
  component: CreatorsProducts,
});

function CreatorsProducts() {
  const [products] = useState<any[]>([]);
  const { theme } = useTheme();

  const colors = {
    dark: {
      text: '#ffffff',
      textMuted: '#9ca3af',
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(102, 126, 234, 0.2)',
      dashedBorder: 'rgba(102, 126, 234, 0.3)',
      buttonBg: '#667eea',
      buttonHover: '#5568d3',
      imageBg: 'rgba(15, 15, 20, 0.6)'
    },
    light: {
      text: '#111827',
      textMuted: '#6b7280',
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      dashedBorder: '#e5e7eb',
      buttonBg: '#2563eb',
      buttonHover: '#1d4ed8',
      imageBg: '#f3f4f6'
    }
  };

  const c = colors[theme];

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}>
          <div>
            <h1 style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              color: c.text,
              marginBottom: '0.5rem'
            }}>
              My Products
            </h1>
            <p style={{ color: c.textMuted, fontSize: '1rem' }}>
              Manage your pedals, samples, and presets
            </p>
          </div>
          <button style={{
            backgroundColor: c.buttonBg,
            color: 'white',
            padding: '0.75rem 1.5rem',
            borderRadius: '0.375rem',
            border: 'none',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '500'
          }}>
            + Create Product
          </button>
        </div>

        {/* Products List */}
        {products.length === 0 ? (
          <div style={{
            background: c.cardBg,
            border: `2px dashed ${c.dashedBorder}`,
            borderRadius: '0.5rem',
            padding: '3rem',
            textAlign: 'center',
            backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none'
          }}>
            <p style={{
              fontSize: '1.125rem',
              color: c.textMuted,
              marginBottom: '1rem'
            }}>
              No products yet
            </p>
            <p style={{
              fontSize: '0.875rem',
              color: c.textMuted,
              marginBottom: '1.5rem',
              opacity: 0.7
            }}>
              Create your first pedal, sample pack, or preset to get started
            </p>
            <button style={{
              backgroundColor: c.buttonBg,
              color: 'white',
              padding: '0.75rem 1.5rem',
              borderRadius: '0.375rem',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '500'
            }}>
              Create Your First Product
            </button>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '1.5rem'
          }}>
            {products.map((product) => (
              <ProductCard key={product.id} product={product} theme={theme} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface ProductCardProps {
  product: any;
  theme: 'dark' | 'light';
}

function ProductCard({ product, theme }: ProductCardProps) {
  const colors = {
    dark: {
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(102, 126, 234, 0.2)',
      imageBg: 'rgba(15, 15, 20, 0.6)',
      text: '#ffffff',
      textMuted: '#9ca3af',
      buttonBg: 'rgba(102, 126, 234, 0.2)',
      buttonText: '#a8b2ff'
    },
    light: {
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      imageBg: '#f3f4f6',
      text: '#111827',
      textMuted: '#6b7280',
      buttonBg: '#f3f4f6',
      buttonText: '#374151'
    }
  };

  const c = colors[theme];

  return (
    <div style={{
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      borderRadius: '0.5rem',
      overflow: 'hidden',
      backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none',
      boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)'
    }}>
      <div style={{
        height: '200px',
        backgroundColor: c.imageBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: c.textMuted
      }}>
        Product Image
      </div>
      <div style={{ padding: '1rem' }}>
        <h3 style={{
          fontSize: '1.125rem',
          fontWeight: '600',
          color: c.text,
          marginBottom: '0.5rem'
        }}>
          {product.name}
        </h3>
        <p style={{
          fontSize: '0.875rem',
          color: c.textMuted,
          marginBottom: '1rem'
        }}>
          {product.description}
        </p>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{
            fontSize: '1.25rem',
            fontWeight: 'bold',
            color: c.text
          }}>
            ${product.price}
          </span>
          <button style={{
            padding: '0.5rem 1rem',
            backgroundColor: c.buttonBg,
            border: 'none',
            borderRadius: '0.375rem',
            cursor: 'pointer',
            fontSize: '0.875rem',
            color: c.buttonText
          }}>
            Edit
          </button>
        </div>
      </div>
    </div>
  );
}
